/**
 * @fileoverview Date utility functions.
 * @summary Centralized date formatting utilities and timezone conversions.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/date
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { getLocaleFromLanguage } from './i18n/index.js'

/**
 * @summary Format date for display with locale support.
 * @param {string | Date} date - Date to format (string or Date object).
 * @param {string} langCode - i18n language code (e.g., 'fr', 'en'). Defaults to 'fr'.
 * @returns {string} Formatted date string.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function formatDate(date: string | Date, langCode: string = 'fr'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const locale = getLocaleFromLanguage(langCode)
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * @summary Convert UTC date (from backend) to local date string for date input (YYYY-MM-DD).
 * @description The date comes from backend as UTC ISO string, we need to display it in user's local timezone.
 * @param {string | Date} utcDate - UTC date from backend (ISO string or Date object).
 * @returns {string} Local date string in format YYYY-MM-DD for date input.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function utcToLocalDateString(utcDate: string | Date): string {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * @summary Convert UTC date (from backend) to local time string for time input (HH:mm).
 * @description The date comes from backend as UTC ISO string, we need to display the time in user's local timezone.
 * @param {string | Date} utcDate - UTC date from backend (ISO string or Date object).
 * @returns {string} Local time string in format HH:mm for time input.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function utcToLocalTimeString(utcDate: string | Date): string {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * @summary Convert local date and time strings to UTC ISO string for backend.
 * @description User enters date/time in local timezone (via date/time inputs), we need to convert to UTC for backend.
 * @param {string} localDate - Local date string in format YYYY-MM-DD from date input.
 * @param {string} localTime - Local time string in format HH:mm from time input.
 * @returns {string} UTC ISO string for backend.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function localToUtcISOString(localDate: string, localTime: string): string {
  // Create Date object from local date/time (browser interprets as local timezone)
  const localDateTime = new Date(`${localDate}T${localTime}`)
  // Convert to UTC ISO string
  return localDateTime.toISOString()
}
